import React, { useEffect, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import DashboardLayout from '../../components/DashboardLayout';
import CohortSelector from '../../components/CohortSelector';
import useCohort from '../../hooks/useCohort';
import AISummary from '../../components/AISummary';
import WorkPlansPage from '../shared/WorkPlansPage';

const STAGE_COLORS = {
  'Problem-Solution Fit':        '#1d4ed8',  // blue — early stage
  'Product-Market Fit':          '#0369a1',  // deeper blue — growing
  'Early Growth & Monetization': '#166534',  // green — monetising
  'Scaling & Regional Expansion':'#065f46',  // deep green — scaling
};

const STAGES = [
  'Problem-Solution Fit',
  'Product-Market Fit',
  'Early Growth & Monetization',
  'Scaling & Regional Expansion',
];

function StageProgressBar({ currentStage, currentProgress, targetStage }) {
  const currentIdx = STAGES.indexOf(currentStage);
  const targetIdx  = STAGES.indexOf(targetStage);

  return (
    <div className="gm-stage-track">
      {STAGES.map((stage, idx) => {
        const color     = STAGE_COLORS[stage];
        const isCurrent = idx === currentIdx;
        const isTarget  = idx === targetIdx;
        const isDone    = idx < currentIdx;

        return (
          <div key={stage} className={`gm-stage-track-segment ${isDone ? 'gm-stage-track-segment--done' : ''} ${isCurrent ? 'gm-stage-track-segment--current' : ''}`}>
            <div className="gm-stage-track-bar">
              {isCurrent && <div className="gm-stage-track-fill" style={{ width: `${currentProgress||0}%`, background: color }} />}
              {isDone    && <div className="gm-stage-track-fill" style={{ width: '100%', background: color, opacity: 0.5 }} />}
            </div>
            <div className="gm-stage-track-label" style={{ color: isCurrent ? color : undefined }}>
              {stage}
              {isCurrent && <span className="gm-stage-track-badge" style={{ background: color }}>Current</span>}
              {isTarget && !isCurrent && <span className="gm-stage-track-badge gm-stage-track-badge--target">Target</span>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function BeneficiaryDashboard() {
  const { profile } = useAuth();
  const { cohorts, selectedCohort, setSelectedCohort } = useCohort(profile);
  const [company, setCompany]         = useState(null);
  const [companyDocId, setCompanyDocId] = useState(null);
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading]         = useState(true);

  useEffect(() => {
    if (!profile?.uid) return;
    async function load() {
      const q = query(collection(db, 'companies'), where('memberUids', 'array-contains', profile.uid));
      const snap = await getDocs(q);
      if (!snap.empty) {
        const compData = snap.docs[0].data();
        setCompany(compData);
        setCompanyDocId(snap.docs[0].id);
        const aSnap = await getDocs(collection(db, 'companies', snap.docs[0].id, 'assessments'));
        setAssessments(aSnap.docs.map(d => d.data()));
      }
      setLoading(false);
    }
    load();
  }, [profile]);

  if (loading) return <DashboardLayout><div className="gm-loading"><div className="gm-spinner" /></div></DashboardLayout>;
  if (!company) return <DashboardLayout><div className="gm-page"><p>No company found. Contact your programme coordinator.</p></div></DashboardLayout>;

  const target   = assessments.find(a => a.type === 'target' && a.status === 'validated');
  const baseline  = assessments.find(a => a.type === 'baseline' && a.status === 'validated');
  const periodic = assessments
    .filter(a => a.type === 'periodic' && a.status === 'validated')
    .sort((a,b) => (b.assessmentDate?.seconds||0) - (a.assessmentDate?.seconds||0));
  // Most recent validated assessment of any kind — periodic takes priority as the
  // freshest signal once it exists, falling back to baseline if no periodic yet.
  const latest = periodic[0] || baseline || null;

  const displayStage    = latest?.stage         || company.currentStage;
  const displayProgress = latest?.stageProgress ?? company.currentStageProgress;
  const displayUpgrade  = latest?.stageUpgrade  || company.currentStageUpgrade;
  const targetStage     = target?.stage         || company.targetStage;
  const targetProgress  = target?.stageProgress ?? company.targetStageProgress;
  const stageColor      = STAGE_COLORS[displayStage] || '#4a5481';

  // Unread coach notes — any assessment with coach notes updated after last read
  const unreadFeedback = assessments.filter(a => {
    if (!a.coachNotes || !a.coachNotesUpdatedAt) return false;
    const updatedAt = a.coachNotesUpdatedAt?.toDate ? a.coachNotesUpdatedAt.toDate() : new Date(a.coachNotesUpdatedAt);
    const readAt    = a.coachNotesReadAt?.toDate   ? a.coachNotesReadAt.toDate()    : null;
    return !readAt || updatedAt > readAt;
  });

  return (
    <DashboardLayout>
      <div className="gm-page">
        <div className="gm-page-header">
          <div>
            <p className="gm-page-welcome">Welcome, {profile?.displayName}</p>
            <h1 className="gm-page-title">{company.name}</h1>
            <CohortSelector cohorts={cohorts} selectedCohort={selectedCohort} onSelect={setSelectedCohort} />
            <p className="gm-page-sub">{company.description}</p>
          </div>
        </div>

        {/* Coach feedback notification */}
        {unreadFeedback.length > 0 && (
          <div className="gm-validation-banner" style={{ background: '#eff6ff', borderColor: '#93c5fd', marginBottom: 24 }}>
            <div className="gm-validation-banner-icon">💬</div>
            <div className="gm-validation-banner-content">
              <div className="gm-validation-banner-title" style={{ color: '#1d4ed8' }}>
                Your coach has provided feedback on {unreadFeedback.length} assessment{unreadFeedback.length > 1 ? 's' : ''}
              </div>
              <div className="gm-validation-banner-items">
                <a href="/scaling/company/assessment" className="gm-validation-banner-link" style={{ color: '#1d4ed8' }}>
                  View feedback →
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Stage hero */}
        <div className="gm-stage-hero" style={{ borderColor: stageColor }}>
          <div className="gm-stage-hero-left">
            <div className="gm-stage-hero-label">Current stage</div>
            <div className="gm-stage-hero-name" style={{ color: stageColor }}>{displayStage}</div>
            <div className="gm-stage-hero-upgrade">{displayUpgrade}</div>
          </div>
          <div className="gm-stage-hero-right">
            <div className="gm-stage-hero-progress-label">Progress within stage</div>
            <div className="gm-stage-hero-pct" style={{ color: stageColor }}>{displayProgress ?? '—'}%</div>
            <div className="gm-mini-progress" style={{ width: 160 }}>
              <div className="gm-mini-progress-fill" style={{ width: `${displayProgress||0}%`, background: stageColor }} />
            </div>
          </div>
        </div>

        {/* Journey track */}
        <div className="gm-section">
          <h2 className="gm-section-title">Scaling journey</h2>
          <div className="gm-card">
            <StageProgressBar
              currentStage={displayStage}
              currentProgress={displayProgress}
              targetStage={targetStage}
              targetProgress={targetProgress}
            />
            <div className="gm-stage-track-legend">
              <span style={{ color: stageColor }}>■ Where you are now · {displayUpgrade}</span>
              <span style={{ color: '#94a3b8' }}>■ Your target · {targetStage}</span>
            </div>
          </div>
        </div>

        {/* Stage comparison */}
        <div className="gm-section">
          <h2 className="gm-section-title">Stage comparison</h2>
          <div className="gm-stage-compare">
            <div className="gm-stage-compare-card">
              <div className="gm-stage-compare-label">Baseline (Immersion week)</div>
              <div className="gm-stage-compare-stage" style={{ color: STAGE_COLORS[baseline?.stage || company.currentStage] }}>{baseline?.stage || company.currentStage || 'Not yet assessed'}</div>
              <div className="gm-stage-compare-upgrade">{baseline?.stageUpgrade || company.currentStageUpgrade}</div>
              <div className="gm-mini-progress" style={{ marginTop: 8 }}>
                <div className="gm-mini-progress-fill" style={{ width: `${(baseline?.stageProgress ?? company.currentStageProgress) || 0}%`, background: STAGE_COLORS[baseline?.stage || company.currentStage] }} />
              </div>
              <div className="gm-mini-progress-label">{baseline?.stageProgress ?? company.currentStageProgress ?? 0}% through stage</div>
              {assessments.find(a => a.type === 'baseline' && a.coachNotes) && (
                <a href="/scaling/company/assessment" className="gm-stage-feedback-link">
                  💬 Coach feedback available →
                </a>
              )}
            </div>

            {latest && (
              <div className="gm-stage-compare-card gm-stage-compare-card--current">
                <div className="gm-stage-compare-label">Latest assessment</div>
                <div className="gm-stage-compare-stage" style={{ color: STAGE_COLORS[latest.stage] }}>{latest.stage}</div>
                <div className="gm-stage-compare-upgrade">{latest.stageUpgrade}</div>
                <div className="gm-mini-progress" style={{ marginTop: 8 }}>
                  <div className="gm-mini-progress-fill" style={{ width: `${latest.stageProgress||0}%`, background: STAGE_COLORS[latest.stage] }} />
                </div>
                <div className="gm-mini-progress-label">{latest.stageProgress}% through stage</div>
                {latest.coachNotes && (
                  <a href="/scaling/company/assessment" className="gm-stage-feedback-link">
                    💬 Coach feedback available →
                  </a>
                )}
              </div>
            )}

            <div className="gm-stage-compare-card gm-stage-compare-card--target">
              <div className="gm-stage-compare-label">Target (Dec 2026)</div>
              <div className="gm-stage-compare-stage" style={{ color: STAGE_COLORS[targetStage] }}>{targetStage}</div>
              <div className="gm-stage-compare-upgrade">{target?.stageUpgrade || company.targetStageUpgrade}</div>
              <div className="gm-mini-progress" style={{ marginTop: 8 }}>
                <div className="gm-mini-progress-fill" style={{ width: `${targetProgress||0}%`, background: STAGE_COLORS[targetStage] }} />
              </div>
              <div className="gm-mini-progress-label">{targetProgress}% through stage</div>
            </div>
          </div>
        </div>

        {/* AI Progress Summary */}
        {companyDocId && (
          <div className="gm-section">
            <h2 className="gm-section-title">Progress summary</h2>
            <AISummary companyId={companyDocId} companyName={company.name} role="beneficiary" />
          </div>
        )}

        {/* Work Plans */}
        {companyDocId && (
          <div className="gm-section">
            <h2 className="gm-section-title">Work plans</h2>
            <WorkPlansPage companyId={companyDocId} isCoach={false} readOnly={false} />
          </div>
        )}

        {/* Quick actions */}
        <div className="gm-section">
          <h2 className="gm-section-title">Quick actions</h2>
          <div className="gm-quick-actions">
            <a href="/scaling/company/assessment" className="gm-action-card">
              <span className="gm-action-icon">📊</span>
              <span className="gm-action-label">
                Submit Assessment
                {unreadFeedback.length > 0 && <span className="gm-action-badge">!</span>}
              </span>
              <span className="gm-action-sub">Record your current state</span>
            </a>
            <a href="/scaling/company/canvas" className="gm-action-card">
              <span className="gm-action-icon">🗂</span>
              <span className="gm-action-label">Business Model Canvas</span>
              <span className="gm-action-sub">Update your canvas</span>
            </a>
            <a href="/scaling/company/journey" className="gm-action-card">
              <span className="gm-action-icon">🗺</span>
              <span className="gm-action-label">Journey Map</span>
              <span className="gm-action-sub">Update your client journey</span>
            </a>
            <a href="/scaling/company/messages" className="gm-action-card">
              <span className="gm-action-icon">💬</span>
              <span className="gm-action-label">Messages</span>
              <span className="gm-action-sub">Contact your coach</span>
            </a>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
